// core/MediaItem/ImageActionsService.ts
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { API_CONFIG } from "@/config/api";

export interface MoveFileRequest {
  fileId: string;
  fileName: string;
  fromStage: string;
  toStage: string;
  contentType?: string;
  newName?: string;
}

export interface MoveFileResponse {
  success: boolean;
  message?: string;
  error?: string;
  // Add other response fields based on your backend implementation
}

export interface MoveBatchFilesRequest {
  files: MoveFileRequest[];
}

export interface MoveBatchFilesResponse {
  success: boolean;
  movedCount: number;
  totalCount: number;
  errors: string[];
  results: Array<{
    fileId: string;
    success: boolean;
    error?: string;
  }>;
}

export interface ApiErrorResponse {
  error: string;
  details?: any;
}

export interface ConversationTurn {
  role: "user" | "model";
  prompt: string;
  imageBase64?: string;
  mimeType?: string;
}

export interface AIEditResponse {
  success: boolean;
  imageBase64?: string;
  mimeType?: string;
  text?: string;
  error?: string;
  modelUsed?: string;
  processingTimeMs?: number;
}

class ImageActionsService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_CONFIG.URL;
  }

  private getAccessToken(): string | null {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        return user.accessToken || null;
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Update media item stage (database-only, no Drive file movement)
   * Uses the new /update-stage endpoint
   */
  async updateStage(
    clientId: string,
    fileId: string,
    toStage: string,
  ): Promise<{ success: boolean; mediaItem: any; message?: string; error?: string }> {
    try {
      console.log(`🔄 API call: Updating stage for file ${fileId} to ${toStage}`);

      const accessToken = this.getAccessToken();
      if (!accessToken) {
        throw new Error("No access token available. Please log in.");
      }

      const response = await fetch(
        `${this.baseUrl}/client/${clientId}/update-stage`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            fileId,
            toStage,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        console.error(`❌ Update stage failed:`, {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          responseData: data,
        });

        const errorMessage =
          data.error ||
          data.message ||
          `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(errorMessage);
      }

      console.log(`✅ API success: Stage updated to ${toStage}`, data);
      return data;
    } catch (error) {
      console.error("❌ API error updating stage:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Move a single file between stages using the backend's move-file endpoint
   */
  async moveFileBetweenStages(
    clientId: string,
    fileData: MoveFileRequest,
  ): Promise<MoveFileResponse> {
    try {
      console.log(
        `🔄 API call: Moving file ${fileData.fileName} from ${fileData.fromStage} to ${fileData.toStage}`,
      );

      // Get access token for authentication
      const accessToken = this.getAccessToken();
      if (!accessToken) {
        throw new Error("No access token available. Please log in.");
      }

      const response = await fetch(
        `${this.baseUrl}/client/${clientId}/move-file`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(fileData),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        console.error(`❌ Move file failed:`, {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          requestData: fileData,
          responseData: data,
        });

        const errorMessage =
          data.error ||
          data.message ||
          `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(errorMessage);
      }

      console.log(`✅ API success: File moved to ${fileData.toStage}`, data);
      return data;
    } catch (error) {
      console.error("❌ API error moving file:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Move multiple files between stages (batch operation)
   * Since the backend doesn't have a batch endpoint, we'll call the single endpoint multiple times
   */
  async moveFilesBetweenStages(
    clientId: string,
    batchData: MoveBatchFilesRequest,
  ): Promise<MoveBatchFilesResponse> {
    try {
      console.log(`🔄 API call: Moving ${batchData.files.length} files`);

      const results: Array<{
        fileId: string;
        success: boolean;
        error?: string;
      }> = [];
      const errors: string[] = [];
      let successCount = 0;

      // Process each file individually
      for (const fileData of batchData.files) {
        try {
          await this.moveFileBetweenStages(clientId, fileData);
          results.push({ fileId: fileData.fileId, success: true });
          successCount++;
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          results.push({
            fileId: fileData.fileId,
            success: false,
            error: errorMessage,
          });
          errors.push(`${fileData.fileName}: ${errorMessage}`);
        }
      }

      const response: MoveBatchFilesResponse = {
        success: successCount > 0,
        movedCount: successCount,
        totalCount: batchData.files.length,
        errors,
        results,
      };

      console.log(
        `✅ API batch complete: Moved ${successCount}/${batchData.files.length} files`,
      );
      return response;
    } catch (error) {
      console.error("❌ API error moving files:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Convenience method: Move image to ready stage
   */
  async moveImageToReady(
    clientId: string,
    image: MediaItem,
  ): Promise<MoveFileResponse> {
    return this.moveFileBetweenStages(clientId, {
      fileId: image.fileIdentity,
      fileName: image.originalFilename,
      fromStage: image.stage || "next",
      toStage: "ready",
      contentType: "photos",
    });
  }

  /**
   * Convenience method: Move image to next stage
   */
  async moveImageToNext(
    clientId: string,
    image: MediaItem,
  ): Promise<MoveFileResponse> {
    return this.moveFileBetweenStages(clientId, {
      fileId: image.fileIdentity,
      fileName: image.originalFilename,
      fromStage: image.stage || "ready",
      toStage: "next",
      contentType: "photos",
    });
  }

  /**
   * Convenience method: Move image to archived stage
   */
  async moveImageToArchived(
    clientId: string,
    image: MediaItem,
  ): Promise<MoveFileResponse> {
    return this.moveFileBetweenStages(clientId, {
      fileId: image.fileIdentity,
      fileName: image.originalFilename,
      fromStage: image.stage || "ready",
      toStage: "archived",
      contentType: "photos",
    });
  }

  /**
   * Convenience method: Move image to dump stage
   */
  async moveImageToDump(
    clientId: string,
    image: MediaItem,
  ): Promise<MoveFileResponse> {
    return this.moveFileBetweenStages(clientId, {
      fileId: image.fileIdentity,
      fileName: image.originalFilename,
      fromStage: image.stage || "ready",
      toStage: "dump",
      contentType: "photos",
    });
  }

  /**
   * Move multiple images to a specific stage
   */
  async moveImagesToStage(
    clientId: string,
    toStage: string,
    images: MediaItem[],
    fromStage?: string,
  ): Promise<MoveBatchFilesResponse> {
    const batchData: MoveBatchFilesRequest = {
      files: images.map((image) => ({
        fileId: image.fileIdentity,
        fileName: image.originalFilename,
        fromStage: fromStage || image.stage || "ready",
        toStage,
        contentType: "photos",
      })),
    };

    return this.moveFilesBetweenStages(clientId, batchData);
  }

  /**
   * Get available stages for an image
   */
  getAvailableStages(currentStage?: string): string[] {
    const allStages = ["ready", "next", "archived", "dump"];
    return allStages.filter((stage) => stage !== currentStage);
  }

  /**
   * Check if stage transition requires grid position
   */
  requiresGridPosition(fromStage: string, toStage: string): boolean {
    return fromStage === "ready" && toStage === "next";
  }

  /**
   * Get prefix action for stage transition
   */
  getPrefixAction(
    fromStage: string,
    toStage: string,
  ): "add" | "remove" | "none" {
    if (fromStage === "ready" && toStage === "next") return "add";
    if (fromStage === "next" && ["ready", "archived", "dump"].includes(toStage))
      return "remove";
    return "none";
  }

  /**
   * Create move request from image
   */
  createMoveFileRequest(
    image: MediaItem,
    toStage: string,
    fromStage?: string,
  ): MoveFileRequest {
    return {
      fileId: image.fileIdentity,
      fileName: image.originalFilename,
      fromStage: fromStage || image.stage || "ready",
      toStage,
      contentType: "photos",
    };
  }

  /**
   * Handle API errors consistently
   */
  private handleError(error: any): Error {
    if (error instanceof Error) {
      return error;
    }

    if (typeof error === "string") {
      return new Error(error);
    }

    if (error && typeof error === "object" && error.error) {
      return new Error(error.error);
    }

    return new Error("An unknown error occurred while moving files");
  }

  /**
   * Reprocess video by queuing it for async processing
   */
  async reprocessVideo(
    clientId: string,
    mediaItemId: number,
  ): Promise<{ success: boolean; message: string; message_id?: string; error?: string }> {
    try {
      console.log(`🔄 API call: Reprocessing video for media item ${mediaItemId}`);

      const { apiClient } = await import("@/services/apiClient");

      const data = await apiClient.post(
        `/client/${clientId}/media/${mediaItemId}/reprocess-video`
      );

      console.log(`✅ API success: Video queued for reprocessing`, data);
      return data;
    } catch (error) {
      console.error("❌ API error reprocessing video:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Reprocess image by queuing it for async processing
   */
  async reprocessImage(
    clientId: string,
    mediaItemId: number,
  ): Promise<{ success: boolean; message: string; message_id?: string; error?: string }> {
    try {
      console.log(`🔄 API call: Reprocessing image for media item ${mediaItemId}`);

      const { apiClient } = await import("@/services/apiClient");

      const data = await apiClient.post(
        `/client/${clientId}/media/${mediaItemId}/reprocess-image`
      );

      console.log(`✅ API success: Image queued for reprocessing`, data);
      return data;
    } catch (error) {
      console.error("❌ API error reprocessing image:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Trigger AI analysis for an image (quality scoring + label generation)
   * Can only be done once per image
   */
  async triggerAiAnalysis(
    clientId: string,
    mediaItemId: number,
  ): Promise<{ success: boolean; message: string; message_id?: string; error?: string }> {
    try {
      console.log(`🔄 API call: Triggering AI analysis for media item ${mediaItemId}`);

      const { apiClient } = await import("@/services/apiClient");

      const data = await apiClient.post(
        `/client/${clientId}/media/${mediaItemId}/trigger-ai-analysis`
      );

      console.log(`✅ API success: Image queued for AI analysis`, data);
      return data;
    } catch (error) {
      console.error("❌ API error triggering AI analysis:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Rotate video clockwise or counterclockwise
   */
  async rotateVideo(
    clientId: string,
    mediaItemId: number,
    direction: "clockwise" | "counterclockwise",
  ): Promise<{ success: boolean; message: string; message_id?: string; error?: string }> {
    try {
      console.log(`🔄 API call: Rotating video ${direction} for media item ${mediaItemId}`);

      const { apiClient } = await import("@/services/apiClient");

      const data = await apiClient.post(
        `/client/${clientId}/media/${mediaItemId}/rotate-video`,
        { direction }
      );

      console.log(`✅ API success: Video queued for rotation ${direction}`, data);
      return data;
    } catch (error) {
      console.error("❌ API error rotating video:", error);
      throw this.handleError(error);
    }
  }

  /**
   * Make a media item publicly accessible via a permanent URL.
   * Copies the optimized file to the public bucket.
   */
  async makePublic(
    clientId: string,
    mediaItemId: number,
  ): Promise<{ publicUrl: string }> {
    try {
      const { apiClient } = await import("@/services/apiClient");
      return await apiClient.post(
        `/client/${clientId}/media/${mediaItemId}/make-public`,
      );
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove public access for a media item.
   * Deletes the file from the public bucket.
   */
  async removePublic(
    clientId: string,
    mediaItemId: number,
  ): Promise<{ success: boolean }> {
    try {
      const { apiClient } = await import("@/services/apiClient");
      return await apiClient.delete(
        `/client/${clientId}/media/${mediaItemId}/make-public`,
      );
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Check API health
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });
      return response.ok;
    } catch (error) {
      console.error("API health check failed:", error);
      return false;
    }
  }

  /**
   * Internal method to upload revision file to backend
   */
  private async uploadRevisionToBackend(
    clientId: string,
    mediaItemId: number,
    file: File,
  ): Promise<{ success: boolean; mediaItem?: any; error?: string }> {
    const { apiClient } = await import("@/services/apiClient");

    const formData = new FormData();
    formData.append('file', file);

    const data = await apiClient.post(
      `/client/${clientId}/media/${mediaItemId}/upload-revision`,
      formData,
      {
        headers: {
          'Content-Type': undefined,
        }
      }
    );

    return data;
  }

  /**
   * Upload a revision and invalidate cache - reusable across all revision upload flows
   * Handles both File uploads and base64 data
   */
  async uploadRevisionComplete(
    clientId: string,
    mediaItemId: number,
    fileIdentity: string,
    source: File | { base64: string; mimeType: string; originalFilename: string },
  ): Promise<{ success: boolean; mediaItem?: any; error?: string }> {
    try {
      console.log(`🔄 API call: Uploading revision for media item ${mediaItemId}`);

      let file: File;

      if (source instanceof File) {
        file = source;
      } else {
        // Convert base64 to File
        const byteCharacters = atob(source.base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: source.mimeType });

        const extension = source.mimeType.split("/")[1] || "png";
        const baseName = source.originalFilename.replace(/\.[^/.]+$/, "");
        const filename = `${baseName}_ai_edited.${extension}`;
        file = new File([blob], filename, { type: source.mimeType });
      }

      // Upload the file
      const result = await this.uploadRevisionToBackend(clientId, mediaItemId, file);

      if (result.success) {
        // Invalidate cache so new image is fetched
        const { fileCacheService } = await import("@/core/cache/fileCacheService");
        await fileCacheService.invalidateFile(fileIdentity);
        await fileCacheService.invalidateFile(`${fileIdentity}_thumb`);
        console.log(`✅ Revision uploaded and cache invalidated for ${fileIdentity}`);
      }

      return result;
    } catch (error) {
      console.error("❌ API error uploading revision:", error);
      throw this.handleError(error);
    }
  }

  /**
   * @deprecated Use uploadRevisionComplete instead
   */
  async uploadRevision(
    clientId: string,
    mediaItemId: number,
    file: File,
  ): Promise<{ success: boolean; mediaItem?: any; error?: string }> {
    return this.uploadRevisionToBackend(clientId, mediaItemId, file);
  }

  /**
   * @deprecated Use uploadRevisionComplete instead
   */
  async uploadRevisionFromBase64(
    clientId: string,
    mediaItemId: number,
    imageBase64: string,
    mimeType: string,
    originalFilename: string,
  ): Promise<{ success: boolean; mediaItem?: any; error?: string }> {
    console.warn("uploadRevisionFromBase64 is deprecated, use uploadRevisionComplete instead");

    const byteCharacters = atob(imageBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    const extension = mimeType.split("/")[1] || "png";
    const baseName = originalFilename.replace(/\.[^/.]+$/, "");
    const filename = `${baseName}_ai_edited.${extension}`;
    const file = new File([blob], filename, { type: mimeType });

    return this.uploadRevisionToBackend(clientId, mediaItemId, file);
  }

  /**
   * Edit an image using AI (Gemini)
   * Supports iterative editing with structured conversation history
   */
  async editImageWithAI(
    clientId: string,
    mediaItemId: number,
    imageBase64: string,
    prompt: string,
    mimeType: string,
    conversationHistory?: ConversationTurn[],
    aspectRatio?: string,
  ): Promise<AIEditResponse> {
    try {
      console.log(`🔄 API call: AI editing image for media item ${mediaItemId}`);

      const { apiClient } = await import("@/services/apiClient");

      const data = await apiClient.post(
        `/client/${clientId}/media/${mediaItemId}/ai-edit`,
        {
          prompt,
          imageBase64,
          mimeType,
          conversationHistory,
          aspectRatio,
          preserveAspectRatio: true,
        },
        { timeout: 120000 }
      );

      if (data.success) {
        console.log(`✅ API success: Image edited with AI`);
      } else {
        console.log(`⚠️ API returned success=false:`, data.error);
      }

      return data;
    } catch (error) {
      console.error("❌ API error editing image with AI:", error);
      throw this.handleError(error);
    }
  }
}

export const imageActionsService = new ImageActionsService();
export default imageActionsService;
